import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { withStyles } from '@material-ui/core/styles';
import { CommonModalStyleV2 } from "../../../styles/commonModalStyle";

const root = document.getElementById('portal');

const mobile = /Mobi|Android/i.test((window && window.navigator && window.navigator.userAgent) || '');

export const modalWidth = {
  xs: 560,
  s: 650,
  m: 950,
  l: 1024,
  xl: 1300
};

const initialPoistion = {
  numeric: {
    top: 8,
    left: 8
  },
  px: {
    top: '8px',
    left: '8px'
  }
};

const animationDuration = '0.2s';

const Event = {
  mouseup: 'mouseup',
  mousedown: 'mousedown',
  mousemove: 'mousemove',

  touchstart: 'touchstart',
  touchend: 'touchend',
  touchmove: 'touchmove'
};

const Component = ({
  title: heading,
  children: content,
  open,
  classes,
  style = {
    width: modalWidth.s
  }
}) => {
  const ref = {
    modal: useRef(),
    heading: useRef(),
    utilityPanel: useRef(),
  };
  const offsetX = useRef(0);
  const offsetY = useRef(0);

  const handleAdjustPosition = useCallback(() => {
    const maxBottom = window.innerHeight - initialPoistion.numeric.top;
    const maxRight = window.innerWidth - initialPoistion.numeric.left;
    const { left, right, top, bottom } = ref.modal.current.getBoundingClientRect();
    const position = { top: `${top}px`, left: `${left}px` };

    if (top < initialPoistion.numeric.top) {
      position.top = initialPoistion.px.top;
    }
    if (left < initialPoistion.numeric.left) {
      position.left = initialPoistion.px.left;
    }
    if (bottom > maxBottom) {
      position.top = `${top - (bottom - maxBottom)}px`;
    }
    if (right > maxRight) {
      position.left = `${left - (right - maxRight)}px`;
    }

    handleSetPosition(position.top, position.left);
  }, []);

  const handleDragging = {
    start: useCallback((event) => {
      if (mobile && event.type === Event.mousedown) {
        return;
      }
  
      const movement = mobile ? event.touches[0] : event;
      const { clientX, clientY } = movement;
      const { offsetLeft, offsetTop } = ref.modal.current;
      offsetX.current = clientX - offsetLeft;
      offsetY.current = clientY - offsetTop;
      ref.heading.current.classList.add('grabbing');
      document.addEventListener(mobile ? Event.touchmove : Event.mousemove, handleDragging.move);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
    end: useCallback((event) => {
      ref.heading.current.classList.remove('grabbing');
      document.removeEventListener(mobile ? Event.touchmove : Event.mousemove, handleDragging.move);
      handleAdjustPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
    move: useCallback((event) => {
      event.preventDefault();
      const movement = mobile ? event.touches[0] : event;
      const { clientX, clientY } = movement;
      const left = `${clientX - offsetX.current}px`;
      const top = `${clientY - offsetY.current}px`;
      ref.modal.current.style.transition = 'none';
      ref.modal.current.style.left = left;
      ref.modal.current.style.top = top;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  };

  const handleSetPosition = useCallback((top = initialPoistion.px.top, left = initialPoistion.px.left) => {
    if (ref.modal.current) {
      ref.modal.current.style.transition = animationDuration;
      ref.modal.current.style.top = top;
      ref.modal.current.style.left = left;
    }
    setTimeout(() => {
      if(ref.modal.current) ref.modal.current.style.transition = 'none';
    }, 300);
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener(mobile ? Event.touchend : Event.mouseup, handleDragging.end);
    }

    return () => {
      document.removeEventListener(mobile ? Event.touchend : Event.mouseup, handleDragging.end);
      document.removeEventListener(mobile ? Event.touchmove : Event.mousemove, handleDragging.move);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    createPortal(
      <div className={classes.modal} style={style} ref={ref.modal}>
        <div className="modal-heading" ref={ref.heading} onTouchStart={handleDragging.start} onMouseDown={handleDragging.start}>
          <p>{heading}</p>
        </div>
        <div className="modal-content">
          {content}
        </div>
      </div>,
      root
    )
  );
};

export const CommonModal = withStyles(CommonModalStyleV2)(Component);
export default CommonModal;
